import { createServerFn } from "@tanstack/react-start";
import { and, eq, inArray } from "drizzle-orm";
import { db } from "~/db";
import { adImageText, adTextOnly, sysFile, sysFileReference } from "~/db/schema";
import { resolveCmsLink } from "./get-content";

export type AdTextOnly = {
  uid: number;
  type: "text-only";
  header: string | null;
  subheader: string | null;
  label: string | null;
  link: string | null;
  resolvedLink?: string | null;
};

export type AdImageText = {
  uid: number;
  type: "image-text";
  header: string | null;
  subheader: string | null;
  label: string | null;
  link: string | null;
  resolvedLink?: string | null;
  imageIdentifier: string | null;
};

export type Ad = AdTextOnly | AdImageText;

export const getAds = createServerFn({ method: "GET" })
  .handler(async (): Promise<Ad[]> => {
    const pid = 19;

    // Fetch text-only ads
    const textOnlyAds = await db
      .select({
        uid: adTextOnly.uid,
        header: adTextOnly.header,
        subheader: adTextOnly.subheader,
        label: adTextOnly.label,
        link: adTextOnly.link,
      })
      .from(adTextOnly)
      .where(
        and(
          eq(adTextOnly.pid, pid),
          eq(adTextOnly.deleted, 0),
          eq(adTextOnly.hidden, 0)
        )
      )
      .execute();

    // Fetch image-text ads first
    const imageTextAdsBase = await db
      .select({
        uid: adImageText.uid,
        header: adImageText.header,
        subheader: adImageText.subheader,
        label: adImageText.label,
        link: adImageText.link,
        image: adImageText.image,
      })
      .from(adImageText)
      .where(
        and(
          eq(adImageText.pid, pid),
          eq(adImageText.deleted, 0),
          eq(adImageText.hidden, 0)
        )
      )
      .execute();

    // Get all adImageText UIDs
    const adImageTextUids = imageTextAdsBase.map((ad) => ad.uid);

    // Fetch all file references for these adImageText UIDs
    // Include tablenames to help identify the correct reference
    const allFileReferences = await db
      .select({
        uid: sysFileReference.uid,
        uidForeign: sysFileReference.uidForeign,
        fieldname: sysFileReference.fieldname,
        uidLocal: sysFileReference.uidLocal,
        tablenames: sysFileReference.tablenames,
        imageIdentifier: sysFile.identifier,
      })
      .from(sysFileReference)
      .leftJoin(sysFile, eq(sysFile.uid, sysFileReference.uidLocal))
      .where(
        and(
          inArray(sysFileReference.uidForeign, adImageTextUids),
          eq(sysFileReference.deleted, 0),
          eq(sysFileReference.hidden, 0)
        )
      )
      .execute();

    // Create a map: adUID -> imageIdentifier
    // In TYPO3 FAL, adImageText.image = 1 just indicates a file reference exists
    // The actual reference is in sys_file_reference with uidForeign = adImageText.uid
    // We use tablenames to filter to only references belonging to tx_lg24_adimagetext
    const adImageMap = new Map<number, string | null>();

    for (const ad of imageTextAdsBase) {
      // Filter references for this ad by:
      // 1. uidForeign = ad.uid (the adImageText record)
      // 2. fieldname = "image"
      // 3. tablenames = "tx_lg24_adimagetext" (to exclude other content types)
      const refsForAd = allFileReferences.filter(
        (ref) =>
          ref.uidForeign === ad.uid &&
          ref.fieldname === "image" &&
          ref.tablenames === "tx_lg24_adimagetext" &&
          ref.imageIdentifier
      );

      if (refsForAd.length === 0) {
        adImageMap.set(ad.uid, null);
      } else {
        // Should typically be exactly one, but if multiple, use the first one
        adImageMap.set(ad.uid, refsForAd[0].imageIdentifier);
      }
    }

    // Build the final result
    const imageTextAds = imageTextAdsBase.map((ad) => ({
      uid: ad.uid,
      header: ad.header,
      subheader: ad.subheader,
      label: ad.label,
      link: ad.link,
      imageIdentifier: adImageMap.get(ad.uid) ?? null,
    }));

    // Resolve links and format ads
    const formattedTextOnlyAds: AdTextOnly[] = await Promise.all(
      textOnlyAds.map(async (ad) => ({
        uid: ad.uid,
        type: "text-only" as const,
        header: ad.header,
        subheader: ad.subheader,
        label: ad.label,
        link: ad.link,
        resolvedLink: ad.link ? await resolveCmsLink(ad.link) : null,
      }))
    );

    const formattedImageTextAds: AdImageText[] = await Promise.all(
      imageTextAds.map(async (ad) => ({
        uid: ad.uid,
        type: "image-text" as const,
        header: ad.header,
        subheader: ad.subheader,
        label: ad.label,
        link: ad.link,
        resolvedLink: ad.link ? await resolveCmsLink(ad.link) : null,
        imageIdentifier: ad.imageIdentifier,
      }))
    );

    return [...formattedTextOnlyAds, ...formattedImageTextAds];
  });

